// Customizable styling for the line/point overlays (world roads, railways,
// hydrography, city points, and RP infrastructure). Colours, a width multiplier, a dash
// pattern for lines and a marker shape for cities; the multiplier scales the
// zoom-responsive base ramp so the default zoom behaviour is preserved and
// "reset" just returns everything to the shipped defaults.

export const OVERLAY_STYLE_KEY = 'onlinemap-overlay-styles'

// Base zoom ramps (the look shipped by default). The width multiplier scales
// the output values; keep these in sync with MapView's layer definitions.
export const ROAD_WIDTH_RAMP = [3, 0.4, 6, 1.2, 10, 2.6]
export const RAIL_WIDTH_RAMP = [3, 0.5, 6, 1.4, 10, 2.6]
export const RIVER_WIDTH_RAMP = [2, 0.45, 5, 0.9, 8, 1.8, 10, 2.6]
export const CITY_RADIUS_RAMP = [100000, 2, 1000000, 3.5, 5000000, 5, 20000000, 7]
export const INFRA_WIDTH_RAMP = [2, 1, 6, 2.5, 10, 4]

/**
 * Zoom-driven width of the RP infrastructure lines, times `weight` — a number,
 * or a per-feature expression (the width multiplier of each type).
 *
 * MapLibre only accepts `zoom` as the input of a TOP-LEVEL interpolate/step:
 * the weight goes inside every stop. `['*', <zoom interpolate>, weight]` makes
 * MapLibre reject the whole layer — the selection halo and the dotted lines
 * then vanished in Construction mode (PP-45).
 */
export const infraWidthExpression = (weight = 1, ramp = INFRA_WIDTH_RAMP) => {
  const expression = ['interpolate', ['linear'], ['zoom']]
  for (let i = 0; i < ramp.length; i += 2) {
    const width = ramp[i + 1]
    expression.push(ramp[i], typeof weight === 'number' ? width * weight : ['*', width, weight])
  }
  return expression
}

export const LINE_DASHES = ['solid', 'dash', 'dot']
export const CITY_SHAPES = ['circle', 'square', 'triangle', 'star']

/** MapLibre line-dasharray for a dash keyword (null = solid, unset). */
export const dashArrayFor = (dash) => {
  if (dash === 'dash') return [2, 2]
  if (dash === 'dot') return [0.4, 1.8]
  return [1, 0] // solid
}

export const DEFAULT_OVERLAY_STYLES = {
  roads: { color: '#c99a52', width: 1, dash: 'solid' },
  railways: { color: '#9ab4c8', width: 1, dash: 'dash' },
  hydrography: { color: '#4a9fd8', width: 1, opacity: 0.72 },
  cities: { color: '#ffffff', width: 1, shape: 'circle' }, // width = radius multiplier
  // Infrastructure is per RP type; entries are filled in lazily as types are
  // seen. `_default` is the fallback for any type without an explicit entry.
  infrastructure: { _default: { color: '#FF8C00', width: 1, dash: 'solid' } },
}

// RP infrastructure types don't exactly match the real-world categories, so
// classify by name: anything road-like inherits the Monde réel roads look,
// anything rail-like the railways look (dashed steel). Unknown types fall
// back to a deterministic distinct colour.
const ROAD_LIKE = /route|autoroute|chauss|highway|road|voie rapide|nationale/i
const RAIL_LIKE = /fer|rail|train|tgv|m[ée]tro|tram|chemin/i

export const infraCategory = (type) => {
  if (type && ROAD_LIKE.test(type)) return 'road'
  if (type && RAIL_LIKE.test(type)) return 'rail'
  return 'other'
}

/**
 * Deterministic default colour for an RP infrastructure type without a real
 * -world analogue, so distinct types still read differently out of the box.
 */
export const defaultInfraColor = (type) => {
  if (!type) return DEFAULT_OVERLAY_STYLES.infrastructure._default.color
  let hash = 0
  for (let i = 0; i < type.length; i++) hash = (hash * 31 + type.charCodeAt(i)) >>> 0
  const hue = (hash * 137.508) % 360
  const sat = 65 + (hash % 20)
  const light = 52 + (hash % 12)
  return hslToHex(hue, sat, light)
}

/** Shipped default style for one RP infrastructure type (Monde réel-like). */
export const defaultInfraStyle = (type) => {
  const cat = infraCategory(type)
  if (cat === 'road') return { color: DEFAULT_OVERLAY_STYLES.roads.color, width: 1, dash: 'solid' }
  if (cat === 'rail') return { color: DEFAULT_OVERLAY_STYLES.railways.color, width: 1, dash: 'dash' }
  return { color: defaultInfraColor(type), width: 1, dash: 'solid' }
}

/** Resolve the effective {color,width,dash} for one infrastructure type. */
export const infraStyleFor = (styles, type) => {
  const map = styles?.infrastructure || {}
  const base = defaultInfraStyle(type)
  return map[type] ? { ...base, ...map[type] } : base
}

/** Scale a numeric zoom/data ramp's output values by a multiplier. */
export const scaleRamp = (ramp, mult) =>
  ramp.map((v, i) => (i % 2 === 1 ? Math.max(0.05, v * mult) : v))

/**
 * Render a city marker shape onto a canvas in the given colour (with a dark
 * outline) and return the ImageData MapLibre expects for addImage. Used for
 * the non-circle city shapes (square / triangle / star) via a symbol layer.
 */
export const cityMarkerImage = (shape, color, size = 26) => {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  const c = size / 2
  const r = size / 2 - 2

  ctx.beginPath()
  if (shape === 'square') {
    const s = r * 1.6
    ctx.rect(c - s / 2, c - s / 2, s, s)
  } else if (shape === 'triangle') {
    ctx.moveTo(c, c - r)
    ctx.lineTo(c + r * 0.9, c + r * 0.7)
    ctx.lineTo(c - r * 0.9, c + r * 0.7)
    ctx.closePath()
  } else if (shape === 'star') {
    for (let i = 0; i < 10; i++) {
      const rad = i % 2 === 0 ? r : r * 0.45
      const a = (Math.PI / 5) * i - Math.PI / 2
      const x = c + rad * Math.cos(a)
      const y = c + rad * Math.sin(a)
      if (i === 0) ctx.moveTo(x, y)
      else ctx.lineTo(x, y)
    }
    ctx.closePath()
  } else {
    ctx.arc(c, c, r, 0, Math.PI * 2)
  }
  ctx.fillStyle = color
  ctx.fill()
  ctx.lineWidth = 1.5
  ctx.strokeStyle = '#1a1a1a'
  ctx.stroke()

  return ctx.getImageData(0, 0, size, size)
}

function hslToHex(h, s, l) {
  s /= 100
  l /= 100
  const k = (n) => (n + h / 30) % 12
  const a = s * Math.min(l, 1 - l)
  const f = (n) => {
    const color = l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))
    return Math.round(255 * color).toString(16).padStart(2, '0')
  }
  return `#${f(0)}${f(8)}${f(4)}`
}
